"use client";

import { useTranslations } from "@i18n/provider";
import { SpeakerIcon } from "@ui/SpeakerIcon";

export type LessonAudioButtonState = "idle" | "loading" | "playing";

export function LessonAudioButton({
  state,
  onClick,
  disabled = false,
  variant = "reading",
  idleLabel,
  className,
}: {
  state: LessonAudioButtonState;
  onClick: () => void;
  disabled?: boolean;
  variant?: "reading" | "speaking";
  idleLabel?: string;
  className?: string;
}) {
  const { t } = useTranslations();
  const isLoading = state === "loading";
  const isPlaying = state === "playing";
  const isBusy = isLoading || isPlaying;
  const isDisabled = disabled || isLoading;

  const palette =
    variant === "speaking"
      ? "bg-speaking-tint text-speaking-fg focus-visible:ring-speaking"
      : "bg-reading-tint text-reading-fg focus-visible:ring-reading";

  const ariaLabel = isLoading
    ? t("lesson.preparingAudio")
    : isPlaying
      ? t("lesson.stopListening")
      : (idleLabel ?? t("lesson.replayTutorFeedback"));

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-pressed={isPlaying}
      aria-busy={isLoading}
      aria-label={ariaLabel}
      className={
        className ??
        `flex h-[2.625rem] w-[2.625rem] shrink-0 items-center justify-center rounded-full transition-transform focus:outline-none focus-visible:ring-2 ${
          isDisabled ? `${palette} cursor-wait opacity-70` : `${palette} hover:scale-105`
        }`
      }
    >
      <SpeakerIcon className={isBusy ? "h-5 w-5 animate-pulse" : "h-5 w-5"} />
    </button>
  );
}
